import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query'

export interface PaginatedQueryParams<TData> extends UseInfiniteQueryOptions<TData[]> {
  key: QueryKey
  fetchFunction: (offset: number, page: number) => Promise<TData[]>
  limit?: number
  onErrorCallback?: (error: Error) => void
  updateKeyWithUser?: boolean
}

const usePaginatedQuery = <TData = any>({
  key,
  fetchFunction,
  limit = 10,
  onErrorCallback,
  updateKeyWithUser = true,
  ...restOfProps
}: PaginatedQueryParams<TData>) => {
  const keyWithUser = updateKeyWithUser ? [key, 'user'] : key

  const queryResult = useInfiniteQuery<TData[]>(
    keyWithUser,
    ({ pageParam = 0 }) => fetchFunction(pageParam * limit, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length !== limit) {
          return undefined
        }

        return allPages.length
      },
      onError: (error: Error) => {
        if (onErrorCallback) {
          onErrorCallback(error)
        }
      },
      ...restOfProps,
    }
  )

  const data = queryResult.data?.pages.flat() ?? []

  return {
    ...queryResult,
    data,
    // Prevent calling fetchNextPage() if there are no more pages to fetch, as this might cause race conditions on mount
    fetchNextPage: () => {
      if (queryResult.hasNextPage) {
        queryResult.fetchNextPage()
      }
    },
  }
}

export default usePaginatedQuery
